/**
 * Keeps React from crashing when something OUTSIDE React has rearranged the DOM it owns.
 *
 * Browser page translation (Chrome/Edge "Translate this page", Safari, Google Translate) and
 * some extensions replace the text nodes React rendered with their own `<font>` wrappers.
 * React still holds the original node, so the next commit that removes or re-orders it calls
 * `removeChild` / `insertBefore` with a node that is no longer a child, the DOM throws
 * `NotFoundError: Failed to execute 'removeChild' on 'Node'`, and the whole page falls to the
 * error boundary — a non-English player taps a button and the game is gone.
 *
 * The guard turns exactly that impossible call into a no-op (removeChild) or an append
 * (insertBefore), the workaround React's own issue tracker settled on (facebook/react#11538).
 * Every other call goes straight through. Each hit is reported as a WARNING with whether the
 * page was translated, so the logs say which cause it was.
 *
 * Install once, before the first render (`main.tsx`).
 */
import { logClientWarning } from '../api/clientLogApi';

let installed = false;

/** Chrome/Edge mark `<html>` with `translated-ltr` / `translated-rtl` while a page is translated. */
export function isPageTranslated(): boolean {
  if (typeof document === 'undefined') return false;
  const { classList } = document.documentElement;
  return classList.contains('translated-ltr') || classList.contains('translated-rtl');
}

function reportForeignMutation(method: 'removeChild' | 'insertBefore'): void {
  try {
    const page = typeof window !== 'undefined' ? window.location.href : 'n/a';
    logClientWarning(
      `[DomGuard] ${method}: node was moved by something outside React`,
      `page=${page}\ntranslated=${isPageTranslated()}`
    );
  } catch {
    // Reporting must never become the crash it is guarding against.
  }
}

export function installDomMutationGuard(): void {
  if (installed || typeof Node === 'undefined') return;
  installed = true;

  const originalRemoveChild = Node.prototype.removeChild;
  Node.prototype.removeChild = function guardedRemoveChild<T extends Node>(this: Node, child: T): T {
    if (child.parentNode !== this) {
      reportForeignMutation('removeChild');
      return child;
    }
    return originalRemoveChild.call(this, child) as T;
  };

  const originalInsertBefore = Node.prototype.insertBefore;
  Node.prototype.insertBefore = function guardedInsertBefore<T extends Node>(
    this: Node,
    newNode: T,
    referenceNode: Node | null
  ): T {
    if (referenceNode && referenceNode.parentNode !== this) {
      reportForeignMutation('insertBefore');
      return originalInsertBefore.call(this, newNode, null) as T;
    }
    return originalInsertBefore.call(this, newNode, referenceNode) as T;
  };
}
